import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  FacebookLoginResponse,
  FacebookLoginOptions,
  FacebookUserInfo,
  FacebookCallbackResponse,
} from 'common/models/facebook';

const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const FACEBOOK_SDK_ID = 'facebook-jssdk';
const GRAPH_API_VERSION = 'v21.0';

interface UseFacebookSDKOptions {
  /** Facebook App ID */
  appId: string;
  /** Auto-initialize SDK when hook mounts (default: true) */
  autoLoad?: boolean;
}

interface UseFacebookSDKReturn {
  /** Whether the SDK has been loaded and initialized */
  isSdkLoaded: boolean;
  /** Whether the SDK is currently loading */
  isLoading: boolean;
  /** Error message if SDK failed to load */
  error: string | null;
  /** Trigger Facebook login popup */
  login: (
    options: FacebookLoginOptions,
    callback: (response: FacebookCallbackResponse) => void
  ) => void;
  /** Get current login status */
  getLoginStatus: (callback: (response: FacebookLoginResponse) => void) => void;
  /** Logout from Facebook */
  logout: (callback?: () => void) => void;
}

// Track SDK loading state globally to prevent multiple loads
let sdkLoadPromise: Promise<void> | null = null;
let isSdkInitialized = false;

/**
 * Custom hook for Facebook SDK integration
 * 
 * Handles dynamic loading of the Facebook JavaScript SDK and provides
 * methods for login, logout, and checking login status.
 * 
 * @example
 * ```tsx
 * const { isSdkLoaded, login, error } = useFacebookSDK({ appId: 'your-app-id' });
 * 
 * const handleLogin = () => {
 *   login({ scope: 'public_profile,email' }, (response) => {
 *     if (response.accessToken) {
 *       // User logged in successfully
 *     }
 *   });
 * };
 * ```
 */
export function useFacebookSDK({
  appId,
  autoLoad = true,
}: UseFacebookSDKOptions): UseFacebookSDKReturn {
  const [isSdkLoaded, setIsSdkLoaded] = useState<boolean>(isSdkInitialized);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  /**
   * Load the Facebook SDK script dynamically
   */
  const loadSdk = useCallback((): Promise<void> => {
    // Return existing promise if SDK is already loading
    if (sdkLoadPromise) {
      return sdkLoadPromise;
    }

    // SDK already loaded and initialized
    if (window.FB && isSdkInitialized) {
      return Promise.resolve();
    }

    sdkLoadPromise = new Promise((resolve, reject) => {
      // Check if script tag already exists
      if (document.getElementById(FACEBOOK_SDK_ID)) {
        // Script exists but FB might not be ready yet
        if (window.FB) {
          if (!isSdkInitialized) {
            window.FB.init({
              appId,
              cookie: true,
              xfbml: false,
              version: GRAPH_API_VERSION,
            });
            isSdkInitialized = true;
          }
          resolve();
        } else {
          // Wait for existing script to load
          const checkFB = setInterval(() => {
            if (window.FB) {
              clearInterval(checkFB);
              if (!isSdkInitialized) {
                window.FB.init({
                  appId,
                  cookie: true,
                  xfbml: false,
                  version: GRAPH_API_VERSION,
                });
                isSdkInitialized = true;
              }
              resolve();
            }
          }, 100);

          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkFB);
            reject(new Error('Facebook SDK load timeout'));
          }, 10000);
        }
        return;
      }

      // Set up async init callback
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: false,
          version: GRAPH_API_VERSION,
        });
        isSdkInitialized = true;
        resolve();
      };

      // Create and inject script tag
      const script = document.createElement('script');
      script.id = FACEBOOK_SDK_ID;
      script.src = FACEBOOK_SDK_URL;
      script.async = true;
      script.defer = true;

      script.onerror = () => {
        sdkLoadPromise = null;
        reject(new Error('Failed to load Facebook SDK'));
      };

      // Insert before first script tag
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    });

    return sdkLoadPromise;
  }, [appId]);

  /**
   * Initialize SDK on mount
   */
  useEffect(() => {
    mountedRef.current = true;

    if (autoLoad && appId) {
      setIsLoading(true);
      loadSdk()
        .then(() => {
          if (mountedRef.current) {
            setIsSdkLoaded(true);
            setIsLoading(false);
            setError(null);
          }
        })
        .catch((err) => {
          if (mountedRef.current) {
            setError(err.message);
            setIsLoading(false);
          }
        });
    }

    return () => {
      mountedRef.current = false;
    };
  }, [appId, autoLoad, loadSdk]);

  /**
   * Trigger Facebook login popup and fetch user info
   */
  const login = useCallback(
    (
      options: FacebookLoginOptions,
      callback: (response: FacebookCallbackResponse) => void
    ) => {
      if (!window.FB) {
        callback({ status: 'unknown' });
        return;
      }

      // Extract fields from options, default to standard user profile fields
      const { fields = 'id,name,email,picture', ...fbLoginOptions } = options;

      window.FB.login(
        (loginResponse: FacebookLoginResponse) => {
          if (loginResponse.status === 'connected' && loginResponse.authResponse) {
            // Fetch user profile data with configurable fields
            window.FB.api(
              '/me',
              { fields },
              (userInfo: FacebookUserInfo) => {
                callback({
                  status: 'connected',
                  accessToken: loginResponse.authResponse!.accessToken,
                  userID: loginResponse.authResponse!.userID,
                  name: userInfo.name,
                  email: userInfo.email,
                  picture: userInfo.picture?.data?.url,
                });
              }
            );
          } else {
            callback({ status: loginResponse.status });
          }
        },
        fbLoginOptions
      );
    },
    []
  );

  /**
   * Get current Facebook login status
   */
  const getLoginStatus = useCallback(
    (callback: (response: FacebookLoginResponse) => void) => {
      if (!window.FB) {
        callback({ status: 'unknown', authResponse: null });
        return;
      }
      window.FB.getLoginStatus(callback);
    },
    []
  );

  /**
   * Logout from Facebook
   */
  const logout = useCallback((callback?: () => void) => {
    if (!window.FB) {
      callback?.();
      return;
    }
    window.FB.logout(() => {
      callback?.();
    });
  }, []);

  return {
    isSdkLoaded,
    isLoading,
    error,
    login,
    getLoginStatus,
    logout,
  };
}

export default useFacebookSDK;
